import { UseFormReturn } from 'react-hook-form';
import { ITaxCodeForm } from '../../../pages/TravelRequest/constants/travelreq.interface';
import { DateInput, NumberInput, SelectInput, TextInput } from '../..';
import { ETaxCodeColumns } from '../../../pages/TravelRequest/constants/travelreq.enum';
import { useGetCountriesQuery, useGetDistributionChannelQuery, useGetStatesQuery } from '../../../store';
import { useMemo } from 'react';
import { mapToDropdownOptions } from '../../../utils';
import { useGetHSNCodesQuery } from '../../../store/rtkQuery/master';

export default function TaxCodeForm({
    form,
}: {
    form: UseFormReturn<ITaxCodeForm>;
}) {
    const { control, formState } = form;

    const hsnCodesResponse = useGetHSNCodesQuery({});
    const statesResponse = useGetStatesQuery();
    const countryResponse = useGetCountriesQuery();
    const { data: distributionChannelsResponse } = useGetDistributionChannelQuery(
        {},
    );

    const hsnCodesDropdown = useMemo(() => {
        const data = hsnCodesResponse.data as any;
        const hsnCodes = data?.data || data?.list || [];
    
        return hsnCodes.map((item: any) => ({
            label:
                item.code && (item.description || item.gstRate)
                    ? `${item.code} - ${item.description || ''} - ${item.gstRate || ''}%`
                    : item.description ||
                      item.code ||
                      item.gstRate ||
                      `HSN ${item.id || item.ID}`,
    
            value: item.id ?? item.ID,
        }));
    }, [hsnCodesResponse.data]);

    const statesDropdown = useMemo(() => {
        if (statesResponse.data) {
            return mapToDropdownOptions(
                statesResponse.data?.data || [],
                ['ShortDescription', 'StateName'],
                'ID',
            );
        }
        return [];
    }, [statesResponse.data]);
    const distributionChannelsOptions = useMemo(() => {
        const data = distributionChannelsResponse as any;
        const channels = data?.data || data?.list || [];
        return channels.map((item: any) => ({
            label:
                item.erpCode && (item.name || item.code)
                    ? `${item.erpCode} - ${item.name || item.code}`
                    : item.name || item.code || item.erpCode || `Channel ${item.id}`,
            value: item.id,
        }));
    }, [distributionChannelsResponse]);
    const countriesDropdown = useMemo(() => {
        const data = countryResponse.data?.data || [];
        return data.map((item: any) => ({
            label: [item.Code, item.ShortDescription].filter(Boolean).join(' - ') || '',
            value: item.ID,
        }));
    }, [countryResponse.data]);

    return (
        <div>
            <form className="flex flex-col gap-4">
                <div className="grid grid-cols-1 gap-4">
                    <SelectInput
                        name={ETaxCodeColumns.COUNTRY}
                        control={control}
                        label="Country"
                        formState={formState}
                        config={{
                            showSearch: true,
                            placeholder: 'Select country',
                            options: countriesDropdown,
                            optionFilterProp: 'label',
                            optionLabelProp: 'label',
                            disabled: false,
                        }}
                    />
                    <SelectInput
                        name={ETaxCodeColumns.STATE}
                        control={control}
                        label="State"
                        formState={formState}
                        config={{
                            showSearch: true,
                            placeholder: 'Select state',
                            options: statesDropdown,
                            optionFilterProp: 'label',
                            optionLabelProp: 'label',
                        }}
                    />
                    <SelectInput
                        name={ETaxCodeColumns.HSN_CODE_ID}
                        control={control}
                        label='HSN Code'
                        formState={formState}
                        config={{
                            showSearch: true,
                            placeholder: 'Select HSN code',
                            options: hsnCodesDropdown,
                            optionFilterProp: 'label',
                            optionLabelProp: 'label',
                            fieldNames: {
                                label: 'label',
                                value: 'value',
                            },
                        }}
                    />
                    <TextInput
                        name={ETaxCodeColumns.NAME}
                        control={control}
                        label='Name *'
                        rules={{ required: '* This field is required' }}
                        formState={formState}
                        config={{
                            placeholder: 'Enter tax code name',
                        }}
                    />
                    <TextInput
                        name={ETaxCodeColumns.DESCRIPTION}
                        control={control}
                        label='Description'
                        formState={formState}
                        config={{
                            placeholder: 'Enter tax code description ',
                        }}
                    />
                    <TextInput
                        name={ETaxCodeColumns.ERPCODE}
                        control={control}
                        label='ERP Code'
                        formState={formState}
                        config={{
                            placeholder: 'Enter ERP code ',
                        }}
                    />
                    <NumberInput
                        name={ETaxCodeColumns.TAX_PERCENTAGE}
                        control={control}
                        formState={formState}
                        label="Tax Percentage"
                        config={{
                            type: 'number',
                            placeholder: 'Enter Tax percentage',
                            precision: 2,
                            min: 0,
                            controls: false,
                        }}
                        addOnClass="w-full"
                    />
                    <DateInput
                        name={ETaxCodeColumns.VALID_FROM}
                        control={control}
                        label={'Valid from'}
                        formState={formState}
                    />
                    <DateInput
                        name={ETaxCodeColumns.VALID_TO}
                        control={control}
                        label={'Valid to'}
                        formState={formState}
                    />
                    <SelectInput
                        name={ETaxCodeColumns.DISTRIBUTION_CHANNEL}
                        label="Distribution Channel"
                        formState={formState}
                        control={control}
                        config={{
                            showSearch: true,
                            placeholder: 'Select country',
                            options: distributionChannelsOptions,
                            optionFilterProp: 'label',
                            optionLabelProp: 'label',
                            disabled: false,
                        }}
                    />
                    <NumberInput
                        name={ETaxCodeColumns.CGST}
                        control={control}
                        formState={formState}
                        label="CGST (%)"
                        config={{
                            type: 'number',
                            placeholder: 'Enter CGST percentage',
                            precision: 2,
                            min: 0,
                            controls: false,
                        }}
                        addOnClass="w-full"
                    />
                    <NumberInput
                        name={ETaxCodeColumns.SGST}
                        control={control}
                        formState={formState}
                        label="SGST (%)"
                        config={{
                            type: 'number',
                            placeholder: 'Enter SGST percentage',
                            precision: 2,
                            min: 0,
                            controls: false,
                        }}
                        addOnClass="w-full"
                    />
                    <NumberInput
                        name={ETaxCodeColumns.IGST}
                        control={control}
                        formState={formState}
                        label="IGST (%)"
                        config={{
                            type: 'number',
                            placeholder: 'Enter IGST percentage',
                            precision: 2,
                            min: 0,
                            controls: false,
                        }}
                        addOnClass="w-full"
                    />
                    <NumberInput
                        name={ETaxCodeColumns.UGST}
                        control={control}
                        formState={formState}
                        label="UGST (%)"
                        config={{
                            type: 'number',
                            placeholder: 'Enter UGST percentage',
                            precision: 2,
                            min: 0,
                            controls: false,
                        }}
                        addOnClass="w-full"
                    />
                </div>
            </form>
        </div>
    );
}
